/**
 * _support_messages.js — pure, DOM-free logic of the support chat view
 * (support_chat_controller.js), tested in tests/js/support_messages.test.mjs.
 *
 * Messages are the server's wire shape (SupportService.serialise_message);
 * nothing here adds fields to them. A message not yet saved carries a string
 * id ('pending-<n>') and sorts after every saved one.
 *
 * Rendering is escape-first, as in the Chip AI widget: the body is escaped and
 * only line breaks become markup, plus, in a staff message, links to pages on
 * this site written as [label](/path) (the composer's link picker writes
 * them). No other markdown and no other URLs.
 */

const TICKET_TITLE_CHARS = 80;

/**
 * A link to a page on this site: [label](/path), the path one "/" followed by
 * neither "/" nor "\" (browsers read "//host" and "/\host" as another site).
 * A relative path opens in whichever shell the reader is in: the web app and
 * both native shells keep same-origin links in their own web view. Matches
 * PAGE_LINK in application/utils/support_links.py, which writes the same
 * links as plain text for push and email.
 */
const PAGE_LINK = /\[([^\]\n]+)\]\((\/(?![/\\])[^\s)\\]*)\)/g;

function isSaved(message) {
    return typeof message.id === 'number';
}

/** existing + incoming, deduped by id (incoming wins), saved ones in id order. */
export function mergeMessages(existing, incoming) {
    const byId = new Map();
    [...existing, ...incoming].forEach((message) => byId.set(message.id, message));
    const all = [...byId.values()];
    const saved = all.filter(isSaved).sort((a, b) => a.id - b.id);
    return [...saved, ...all.filter((message) => !isSaved(message))];
}

/** Whether a message sits on the viewer's side: their own as a customer, any staff's as staff. */
export function isOwnSide(message, mode, meId) {
    if (mode === 'staff') return Boolean(message.is_staff_sender);
    return message.sender_user_id === meId;
}

function localDay(message) {
    const when = message.created_at ? new Date(message.created_at) : new Date();
    return new Date(when.getFullYear(), when.getMonth(), when.getDate());
}

function dayLabel(day, now) {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const days = Math.round((today - day) / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    return day.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

/**
 * The view as a list of items: day separators ({type: 'day', label}), system
 * lines ({type: 'system', message}) and runs of one sender's messages
 * ({type: 'run', own, senderId, messages}), in the viewer's local dates.
 */
export function groupMessages(messages, mode, meId, now = new Date()) {
    const items = [];
    let lastDay = null;
    let run = null;
    messages.forEach((message) => {
        const day = localDay(message);
        if (!lastDay || day.getTime() !== lastDay.getTime()) {
            items.push({ type: 'day', label: dayLabel(day, now) });
            lastDay = day;
            run = null;
        }
        if (message.kind === 'system') {
            items.push({ type: 'system', message });
            run = null;
        } else if (run && run.senderId === message.sender_user_id) {
            run.messages.push(message);
        } else {
            run = { type: 'run', own: isOwnSide(message, mode, meId),
                    senderId: message.sender_user_id, messages: [message] };
            items.push(run);
        }
    });
    return items;
}

/**
 * How far a viewer who sees the thread has read: the newest saved message,
 * whoever sent it. The server counts system lines and a colleague's replies as
 * unread (ConversationService.unread_count), so the cursor must pass them; my
 * own messages are already behind it, and it never moves backwards.
 */
export function readUpToId(messages) {
    const saved = messages.filter(isSaved);
    return saved.length ? saved[saved.length - 1].id : null;
}

/**
 * Where a send goes, under /api/support: a customer always posts to their own
 * thread; staff reply in the open thread, or, on a new conversation with a
 * customer who has none yet, post to that customer, which creates it.
 */
export function postPath(mode, conversationId, recipientId) {
    if (mode !== 'staff') return '/messages';
    if (!conversationId && recipientId) return `/customers/${recipientId}/messages`;
    return `/conversations/${conversationId}/messages`;
}

/** Id of my side's latest saved message the other side has read up to, or null. */
export function seenMessageId(messages, mode, meId, otherReadId) {
    if (!otherReadId) return null;
    const read = messages.filter((m) => isSaved(m) && m.kind !== 'system'
        && isOwnSide(m, mode, meId) && m.id <= otherReadId);
    return read.length ? read[read.length - 1].id : null;
}

export function typingLabel(firstName) {
    return `${firstName || 'Someone'} is typing...`;
}

/** Title and description for a ticket raised from a message. */
export function ticketPrefill(message) {
    const body = message.body || '';
    return {
        title: body ? body.slice(0, TICKET_TITLE_CHARS) : 'Problem with a photo',
        description: body,
    };
}

/**
 * The image a paste into the message box carries, or null. A paste that also
 * carries text (a copied document often brings a picture of itself along) is
 * left to paste as text.
 */
export function pastedImage(clipboardData) {
    if (!clipboardData || clipboardData.getData('text/plain')) return null;
    return Array.from(clipboardData.files || []).find((file) => file.type.startsWith('image/')) || null;
}

export function escapeHtml(text) {
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * A message body as markup: escaped, with line breaks kept and, when `links`,
 * page links as links. The pattern runs on the escaped text, so label and path
 * are already safe.
 */
export function renderBody(body, links = false) {
    if (!body) return '';
    let html = escapeHtml(body);
    if (links) {
        html = html.replace(PAGE_LINK, (_match, label, path) =>
            `<a href="${path}" class="support-link">${label}</a>`);
    }
    return html.replace(/\n/g, '<br>');
}

/** A message's text as markup: only staff can link to a page. */
export function renderMessageBody(message) {
    return renderBody(message.body, Boolean(message.is_staff_sender));
}

/** A body as plain text, each page link read as its label (for previews). */
export function linkLabels(body) {
    return body.replace(PAGE_LINK, '$1');
}
